import { IonContent, IonPage, IonSpinner, useIonActionSheet } from '@ionic/react';
import { useState } from 'react';
import { Redirect } from 'react-router-dom';

import type { Company } from '~/api/auth';
import {
  type CompanyInvitation,
  type CompanyUser,
  INVITATION_DELIVERY_LABEL,
  INVITATION_DELIVERY_PROBLEMS,
  INVITATION_EXPIRED_STATUS,
  companyTeamRefusalOf,
  invitationBouncedNotice,
  isInvitationBounced,
} from '~/api/companyUsers';
import { useCompany, useModuleLevel } from '~/api/hooks/useCompany';
import {
  useCompanyInvitationsQuery,
  useCompanyUsersQuery,
  useResendCompanyInvitation,
  useRevokeCompanyInvitation,
} from '~/api/hooks/useCompanyUsers';
import { AccessLevelTag } from '~/components/AccessLevelTag';
import { CompanySwitcher } from '~/components/CompanySwitcher';
import { ShowMoreButton } from '~/components/ShowMoreButton';
import { Avatar, initialsFrom } from '~/components/ui/Avatar';
import { Icon } from '~/components/ui/Icon';
import { SectionHead } from '~/components/ui/SectionHead';
import { useAppNavigation } from '~/hooks/useAppNavigation';
import { useNetworkStatus } from '~/hooks/useNetworkStatus';
import { useRevealedRows } from '~/hooks/useRevealedRows';
import { formatDateTime } from '~/lib/format';
import { ROUTES, empresaUsuarioPath } from '~/routes';
import {
  ACCESS_LEVEL,
  COMPANY_ROLE_LABEL,
  FISCAL_MODULES,
  type FiscalModuleKey,
  atLeast,
} from '~/session/access';
import { useAuthStore } from '~/stores/auth';

const RESENT_WITHOUT_EMAIL =
  'O convite foi renovado, mas o e-mail não saiu. Tente reenviar mais tarde.';
const CARD_CLASS = 'rounded-[18px] border-hair border-rule bg-bg-surface p-4';
const ACTION_CLASS =
  'rounded-[10px] border-hair border-rule px-3 py-1.5 text-[12px] font-medium text-ink disabled:opacity-40';
/** "Revogar" em destaque: o convite devolvido se conserta revogando e convidando o endereço certo. */
const ACTION_FORWARD_CLASS =
  'rounded-[10px] border-hair border-negative bg-negative-tint px-3 py-1.5 text-[12px] font-semibold text-negative disabled:opacity-40';
const DELIVERY_PROBLEM_CLASS = 'mt-0.5 text-[12px] font-medium text-negative';
const DELIVERY_NOTE_CLASS = 'mt-0.5 text-[12px] text-ink-muted';

/** Só os módulos em que a pessoa alcança algo: a lista mostra acesso, não a grade inteira. */
function grantedModules(access: CompanyUser['access']): FiscalModuleKey[] {
  return FISCAL_MODULES.filter((module) => atLeast(access[module], ACCESS_LEVEL.read));
}

/** O papel é opcional no schema (o convite da equipe do escritório não tem); o de empresa sempre o traz. */
function invitationRoleLabel(role: CompanyInvitation['role']): string {
  return role === '' ? 'Convidado' : COMPANY_ROLE_LABEL[role];
}

function deadlineLabel(invitation: CompanyInvitation): string {
  const when = formatDateTime(invitation.expires_at);
  return invitation.status === INVITATION_EXPIRED_STATUS ? `Venceu em ${when}` : `Vence em ${when}`;
}

function deliveryLineClass(invitation: CompanyInvitation): string {
  return INVITATION_DELIVERY_PROBLEMS.includes(invitation.delivery_status)
    ? DELIVERY_PROBLEM_CLASS
    : DELIVERY_NOTE_CLASS;
}

/** O id da linha de entrega, que descreve o "Revogar" em destaque para quem usa leitor de tela. */
function deliveryLineId(invitation: CompanyInvitation): string {
  return `convite-${invitation.id}-entrega`;
}

/** O reenvio que devolveu na hora não pede outro reenvio: o endereço é que está errado. */
function resendNoticeOf(renewed: CompanyInvitation): string {
  if (isInvitationBounced(renewed)) return invitationBouncedNotice(renewed.email);
  return renewed.email_sent ? `Convite reenviado para ${renewed.email}.` : RESENT_WITHOUT_EMAIL;
}

export function EmpresaUsuarios(): JSX.Element {
  const { selected } = useCompany();

  return (
    <IonPage>
      <IonContent fullscreen>
        <div className="rx-container rx-screen pb-7">
          {selected ? <Team company={selected} /> : <Redirect to={ROUTES.dashboard} />}
        </div>
      </IonContent>
    </IonPage>
  );
}

function Team({ company }: { company: Company }): JSX.Element {
  const nav = useAppNavigation();
  const { isOnline } = useNetworkStatus();
  const signedInUserId = useAuthStore((state) => state.user?.id);
  const [presentActionSheet] = useIonActionSheet();
  const [notice, setNotice] = useState<string | null>(null);

  // O portão não fica só no `RequireModule`: numa empresa em que a conta é `Funcionário`, a
  // consulta nem sai (o backend responderia 403).
  const canManageTeam = atLeast(useModuleLevel('company_users'), ACCESS_LEVEL.full);
  const membersQuery = useCompanyUsersQuery(company.id, { enabled: canManageTeam });
  const invitationsQuery = useCompanyInvitationsQuery(company.id, { enabled: canManageTeam });
  const resend = useResendCompanyInvitation(company.id);
  const revoke = useRevokeCompanyInvitation(company.id);

  const canAct = isOnline && !resend.isPending && !revoke.isPending;
  const invitations = invitationsQuery.data ?? [];
  const shownMembers = useRevealedRows(membersQuery.data ?? []);
  const shownInvitations = useRevealedRows(invitations);

  const confirmResend = (invitation: CompanyInvitation): void => {
    presentActionSheet({
      header: `Reenviar o convite de ${invitation.email}?`,
      subHeader: 'O link anterior para de valer na hora.',
      buttons: [
        {
          text: 'Reenviar agora',
          handler: () => {
            setNotice(null);
            resend.mutate(invitation.id, {
              onSuccess: (renewed) => setNotice(resendNoticeOf(renewed)),
              onError: (error) => setNotice(companyTeamRefusalOf(error).message),
            });
          },
        },
        { text: 'Fechar', role: 'cancel' },
      ],
    });
  };

  const confirmRevoke = (invitation: CompanyInvitation): void => {
    presentActionSheet({
      header: `Revogar o convite de ${invitation.email}?`,
      subHeader: 'O link para de valer e o endereço fica livre para um convite novo.',
      buttons: [
        {
          text: 'Revogar convite',
          role: 'destructive',
          handler: () => {
            setNotice(null);
            revoke.mutate(invitation.id, {
              onSuccess: () => setNotice('Convite revogado.'),
              onError: (error) => setNotice(companyTeamRefusalOf(error).message),
            });
          },
        },
        { text: 'Fechar', role: 'cancel' },
      ],
    });
  };

  return (
    <>
      <div className="px-6 pt-3">
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            className="rx-iconbtn"
            onClick={() => nav.back()}
            aria-label="Voltar"
          >
            <Icon name="back" size={18} />
          </button>
          <CompanySwitcher compact />
        </div>
        <h1 className="mt-3.5 text-[30px] font-medium tracking-display-tight text-ink">
          Usuários da empresa
        </h1>
        <p className="mt-1 text-[13px] text-ink-muted">
          Quem entra no aplicativo por esta empresa e com qual acesso.
        </p>
      </div>

      <SectionHead
        label="Equipe"
        action={
          <button
            type="button"
            className={ACTION_CLASS}
            onClick={() => nav.goTo(ROUTES.empresaUsuariosConvidar)}
          >
            Convidar
          </button>
        }
      />

      <div className="flex flex-col gap-3 px-6">
        {membersQuery.isPending ? (
          <Loading label="Carregando a equipe" />
        ) : (
          shownMembers.visible.map((member) => (
            <MemberCard
              key={member.id}
              member={member}
              isSelf={member.user.id === signedInUserId}
              onOpen={() => nav.goTo(empresaUsuarioPath(member.id))}
            />
          ))
        )}
        <ShowMoreButton
          hiddenCount={shownMembers.hiddenCount}
          listLabel="usuários"
          onShowMore={shownMembers.showMore}
        />
      </div>

      <SectionHead label="Convites em aberto" />

      {notice && <output className="block px-6 pb-2 text-[13px] text-ink">{notice}</output>}

      <div className="flex flex-col gap-3 px-6">
        {invitationsQuery.isPending && <Loading label="Carregando os convites" />}
        {!invitationsQuery.isPending && invitations.length === 0 && (
          <p className="text-[13px] text-ink-muted">Nenhum convite em aberto.</p>
        )}
        {shownInvitations.visible.map((invitation) => (
          <InvitationCard
            key={invitation.id}
            invitation={invitation}
            canAct={canAct}
            onResend={() => confirmResend(invitation)}
            onRevoke={() => confirmRevoke(invitation)}
          />
        ))}
        <ShowMoreButton
          hiddenCount={shownInvitations.hiddenCount}
          listLabel="convites"
          onShowMore={shownInvitations.showMore}
        />
      </div>
    </>
  );
}

interface MemberCardProps {
  member: CompanyUser;
  isSelf: boolean;
  onOpen(): void;
}

/**
 * O vínculo de alguém da empresa. O próprio vínculo não abre: ninguém muda o seu acesso
 * (`SelfAccessChangeRefused`), para a empresa não ficar sem quem a administre.
 */
function MemberCard({ member, isSelf, onOpen }: MemberCardProps): JSX.Element {
  const granted = grantedModules(member.access);
  const content = (
    <>
      <div className="flex items-center gap-3">
        <Avatar initials={initialsFrom(member.user.full_name, 2)} size={38} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-medium text-ink">{member.user.full_name}</div>
          <div className="truncate text-[12px] text-ink-muted">{member.user.email}</div>
        </div>
        {isSelf && <span className="rx-tag">Você</span>}
        {!member.is_active && <span className="rx-tag rx-tag-warn">Inativo</span>}
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        <span className="rx-tag">{COMPANY_ROLE_LABEL[member.role]}</span>
        {granted.length === 0 ? (
          <span className="rx-tag">Sem acesso</span>
        ) : (
          granted.map((module) => (
            <AccessLevelTag key={module} module={module} level={member.access[module]} />
          ))
        )}
      </div>
    </>
  );

  if (isSelf) {
    return <article className={CARD_CLASS}>{content}</article>;
  }

  // Sem `aria-label`: o nome acessível do botão é o próprio cartão, então quem usa leitor de
  // tela ouve de quem é o vínculo e o que ele alcança, não só "abrir".
  return (
    <button type="button" className={`${CARD_CLASS} text-left`} onClick={onOpen}>
      {content}
    </button>
  );
}

interface InvitationCardProps {
  invitation: CompanyInvitation;
  canAct: boolean;
  onResend(): void;
  onRevoke(): void;
}

function InvitationCard({
  invitation,
  canAct,
  onResend,
  onRevoke,
}: InvitationCardProps): JSX.Element {
  const deliveryLabel = INVITATION_DELIVERY_LABEL[invitation.delivery_status];
  const isBounced = isInvitationBounced(invitation);
  return (
    <article className={CARD_CLASS}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[14px] text-ink">{invitation.email}</div>
          <div className="mt-0.5 text-[12px] text-ink-muted">{deadlineLabel(invitation)}</div>
          {deliveryLabel && (
            <div id={deliveryLineId(invitation)} className={deliveryLineClass(invitation)}>
              {deliveryLabel}
            </div>
          )}
        </div>
        {invitation.status === INVITATION_EXPIRED_STATUS && (
          <span className="rx-tag rx-tag-warn">Vencido</span>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <span className="rx-tag">{invitationRoleLabel(invitation.role)}</span>
      </div>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          className={ACTION_CLASS}
          aria-label={`Reenviar convite de ${invitation.email}`}
          disabled={!canAct}
          onClick={onResend}
        >
          Reenviar
        </button>
        <button
          type="button"
          className={isBounced ? ACTION_FORWARD_CLASS : ACTION_CLASS}
          aria-label={`Revogar convite de ${invitation.email}`}
          aria-describedby={isBounced ? deliveryLineId(invitation) : undefined}
          disabled={!canAct}
          onClick={onRevoke}
        >
          Revogar
        </button>
      </div>
    </article>
  );
}

function Loading({ label }: { label: string }): JSX.Element {
  return (
    <output className="flex items-center gap-2">
      <IonSpinner name="dots" aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </output>
  );
}
